import React, { useState, useEffect, useMemo } from 'react'
import Comments from '@components/app/comments'
import Curve from '@components/app/curve'
import AboutEditor from '@components/app/editor/about'
import Share from '@components/app/share'
import Showmore from '@components/app/show-more'
import Waveform from '@components/app/waveform'
import Button from '@components/common/button'
import BuyModal from '@components/modals/buy'
import UIHiresBadge from '@components/ui/hires'
import UISample from '@components/ui/sample'
import { getMediaDownload } from '@data/api/download'
import PlayerCtx from '@data/contexts/player'
import classnames from 'classnames'
import Link from 'next/link'
import Router from 'next/router'
import { FormattedMessage, FormattedNumber, FormattedDate } from 'react-intl'
import useMediaQuery from '@/hooks/media-query'
import CdnImage from '@/components/app/cdn-image'
import { AlbumProps } from '../../../../types/album'
import Coverart from '../coverart'
import styles from '../media.module.scss'
import Userinfo from '../userinfo'
import BtnPlayAll from './play'
// import Clap from '@components/app/clap'


interface Props {
  openBuy: boolean
  defaultAlbum: AlbumProps
  toggleEdit: () => void
  isMine: boolean
}

const Album = ({ openBuy, defaultAlbum, toggleEdit, isMine }: Props) => {
  const [ isBuyOpen, setBuyOpen ] = useState(openBuy)
  const [ isSoundBuyOpen, setSoundBuyOpen ] = useState(false)
  const [ buySound, setBuySound ] = useState(null)
  const [ downloadId, setDownload ] = useState()
  const [ soundDownloadIds, setSoundDownload ] = useState([])
  const [ album, setAlbum ] = useState<AlbumProps>(defaultAlbum)
  const [ shareSound, setShareSound ] = useState('')

  const { isMobile } = useMediaQuery()

  const { nowPlaying } = PlayerCtx.useContainer()

  const coverartBlur = album.coverart_blur_url
    ? album.coverart_blur_url.replace('1000/500/blur/250', '500/500/blur/20')
    : ''
  const onSale = album.sales && album.sales.on

  const [ playingAlbumSound, currentIndex ] = useMemo(() => {
    const playingAlbumSoundIndex = album.sounds
      .findIndex((s) => s.id === nowPlaying.track.id)
    if (playingAlbumSoundIndex === -1) { // 再生中の曲がアルバムに含まれていない場合
      return [ null, 0 ]
    }
    return [ album.sounds[playingAlbumSoundIndex], playingAlbumSoundIndex ]
  }, [ nowPlaying, album.sounds ])

  const buyTrack = (sound) => {
    setBuySound(sound)
    setSoundBuyOpen(true)
  }

  const checkDownload = async () => {
    const result = await getMediaDownload(album.id)

    if (!result.purchased || result.error) {
      return
    }
    setDownload(result.downloadId)

    if (!album.sounds || album.sounds.length <= 0) {
      return
    }

    await Promise.all(
      album.sounds.map((sound, index) => {
        album.sounds[index].limit.on = false
        return 0
      }),
    )
    setAlbum(album)
  }

  const checkDownloadSounds = async () => {
    if (!album.sounds || album.sounds.length <= 0) {
      return
    }

    const current = []
    await Promise.all(
      album.sounds.map(async (sound, index) => {
        const result = await getMediaDownload(sound.id)
        if (!result.downloadId || result.error) {
          return
        }
        current.push(result.downloadId)
        album.sounds[index].limit.on = false
      }),
    )
    setSoundDownload(current)
  }

  useEffect(() => {
    checkDownload()
    checkDownloadSounds()
  }, [])

  return (
    <div className={styles.mediaContainer}>
      <style jsx>
        {`
          .album-cover {
            background-image: url(${coverartBlur});
          }
        `}
      </style>
      <div className={`album-cover ${styles.mediaCover}`} />
      <Curve className={styles.coverartBlurContainer}>
        <CdnImage
          src={coverartBlur}
          alt=""
          className={styles.coverartBlurImage}
          width={500}
          height={500}
        />
      </Curve>
      <div className="container-md">
        <div className={`d-flex ${styles.header}`}>
          <div className={`${styles.coverartContainer}`}>
            <div className="ps-relative d-inline-block m-auto">
              <Coverart src={album.coverart_crop_url} alt={album.title} />
            </div>
            {isMine && (
              <div className="mt-5">
                <Button className="lg" onClick={toggleEdit}>
                  <FormattedMessage
                    id="album.edit.btnLabel"
                    defaultMessage="Edit this Album"
                  />
                </Button>
              </div>
            )}
          </div>
          <div className={`${styles.heading} mb-5`}>
            <div className={styles.headingText}>
              <div className="d-flex align-items-center mb-4">
                <div className="flex0 mr-3">
                  <BtnPlayAll album={album} index={currentIndex} isAll />
                </div>
                <div className="flex1">
                  <h1 className={`${styles.title} tx-24 tx-w700 mb-1`}>
                    {album.title}
                  </h1>
                  {album.subtitle && (
                    <h2 className={`${styles.subtitle} tx-16 tx-w300`}>
                      {album.subtitle}
                    </h2>
                  )}
                  {album.released_at && (
                    <span className={`${styles.released} tx-w300 tx-14`}>
                      <span>Released</span>
                      <span className="dot" />
                      <time>
                        <FormattedDate value={album.released_at} />
                      </time>
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {
              album.sounds[currentIndex] && (
                <Waveform
                  id={album.sounds[currentIndex].id}
                  duration={album.sounds[currentIndex].metadata.duration}
                  src={album.sounds[currentIndex].waveform_url}
                  className={styles.waveform}
                  outerColor={
                    isMobile ? 'rgba(0, 0, 0, .3)' : 'rgba(255, 255, 255, .6)'
                  }
                  static={album.sounds[currentIndex].limit.on}
                />
              )
            }
            <div className="d-md-flex align-items-center">
              <div className="flex1 my-md-0 my-5">
                {/* <Clap
                  albumId={album.id}
                  clap={album.userClaps}
                  total={album.claps}
                  users={album.clapUsers}
                /> */}
              </div>
              <div className="flex0">
                <Share
                  type="album"
                  id={album.id}
                  url={`https://sdn.lv/a${album.id}`}
                  text={`${album.title} by ${album.user.name}`}
                />
              </div>
            </div>
            <div className="hr" />
            {album.description && (
              <Showmore
                defaultHeight={116}
                className={`${styles.description} tx-g800 tx-14 tx-l16`}
              >
                <AboutEditor about={album.description} readOnly />
              </Showmore>
            )}
          </div>
        </div>
        <div className="row">
          <div className="col-md-4 order-md-2 mb-5">
            {onSale && (
              <>
                <h2 className="heading-sm tx-oswald tx-w400 tx-20">
                  <span className="text">
                    <span className="solicon-tag-cord mr-1 align-baseline" />
                    On Sale
                  </span>
                </h2>
                {!downloadId ? (
                  <p className="tx-center tx-14 mb-2">
                    <FormattedMessage
                      id="app.buy.albumMessage"
                      defaultMessage="Buy this album to support {name}."
                      values={{
                        name: album.user.name,
                        br: <br />,
                        // eslint-disable-next-line react/display-name
                        strong: (...chunks) => <strong>{chunks}</strong>,
                      }}
                    />
                  </p>
                ) : (
                  <p className="tx-center tx-14 mb-4">
                    <FormattedMessage
                      id="app.buy.purchased.albumMessage"
                      defaultMessage="You've already purchased this album"
                    />
                  </p>
                )}
                {!downloadId && (
                  <div className="tx-center mb-4">
                    <i className="solicon-yen tx-18 mr-1 align-baseline" />
                    <span className="tx-w700 tx-32 tx-oswald">
                      <FormattedNumber
                        value={album.sales.price ?? album.price ?? 0}
                      />
                    </span>
                    {album.sales.nameyourprice && (
                      <span className="ml-1">
                        <FormattedMessage
                          id="buy.more"
                          defaultMessage="or more"
                        />
                      </span>
                    )}
                  </div>
                )}
                <div className="tx-center mb-5">
                  {!downloadId ? (
                    <Button
                      className="lg primary-alt shadow"
                      onClick={() => setBuyOpen(true)}
                      disabled={isMine}
                    >
                      <FormattedMessage
                        id="app.buy.album"
                        defaultMessage="Buy This Album"
                      />
                    </Button>
                  ) : (
                    <Button
                      className="lg primary-alt shadow"
                      onClick={() =>
                        Router.push(
                          `/download?id=${downloadId}`,
                          `/download/${downloadId}`,
                        )
                      }
                    >
                      <FormattedMessage
                        id="download.button"
                        defaultMessage="Download"
                      />
                    </Button>
                  )}
                </div>
                <div className="hr" />
              </>
            )}
            <Userinfo
              name={album.user.name}
              username={album.user.username}
              imageUrl={album.user.user_image}
              shortBio={album.user.shortBio}
            />
          </div>{' '}
          {/* end right column */}
          <div className="col-md-8">
            <h2 className="heading">
              <span className="text">Tracks</span>
            </h2>
            <ol className={`${styles.tracks} mb-5`}>
              {album.sounds.map((sound, i) => (
                <li
                  key={sound.id}
                  className={classnames(
                    'border-b100 mb-1 pb-3',
                    styles.trackItem,
                    { [styles.active]: playingAlbumSound?.id === sound.id },
                  )}
                >
                  <div className="d-flex">
                    <div className="flex0 mr-3">
                      <span
                        className={`tx-center tx-oswald tx-16 tx-w600 tx-g400 ${styles.index}`}
                      >
                        {i + 1}
                      </span>
                      <BtnPlayAll
                        size="2rem"
                        className={`${styles.playTrack}`}
                        album={album}
                        index={i}
                      />
                    </div>
                    <div className="flex1">
                      <header className="d-flex align-items-center">
                        <h3 className="tx-20">
                          <Link
                            href={`/sound/${sound.id}/${sound.slug}`}
                            className="alt"
                          >
                            {sound.title}
                          </Link>
                        </h3>
                      </header>
                      {sound.description && (
                        <p className="tx-14 mb-2">
                          {sound.description.substr(0, 50)}
                          {sound.description.length > 50 && '...'}
                        </p>
                      )}
                      {(sound.hi_resolution ||
                        (sound.limit && sound.limit.on)) && (
                        <div className="d-flex align-items-center mb-2">
                          {sound.hi_resolution && (
                            <UIHiresBadge className="mr-2" />
                          )}
                          {sound.limit && sound.limit.on && <UISample />}
                        </div>
                      )}
                      <Waveform
                        className={`mb-2 ${styles.waveform}`}
                        height={30}
                        id={sound.id}
                        duration={sound.metadata.duration}
                        src={sound.waveform_url}
                        static={sound.limit.on}
                      />
                      <div className={`${styles.actions} ps-relative`}>
                        {sound.claps > 0 && (
                          <button
                            type="button"
                            className="btn-chromeless tx-black tx-12 mr-3"
                          >
                            <i className="solicon-heart mr-2" />
                            {sound.claps}
                          </button>
                        )}
                        {sound.comments > 0 && (
                          <button
                            type="button"
                            className="btn-chromeless tx-black tx-12 mr-3"
                          >
                            <i className="solicon-comment-alt mr-2" />
                            {sound.comments}
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn-chromeless tx-black tx-12 mr-3"
                          onClick={() => setShareSound((current) => current !== sound.id ? sound.id : '')}
                        >
                          <i className="solicon-share mr-2" />
                          {shareSound === sound.id ? (
                            <FormattedMessage
                              id="app.stopshare"
                              defaultMessage="Stop Sharing"
                            />
                          ) : (
                            <FormattedMessage id="app.share" defaultMessage="Share" />
                          )}
                        </button>
                        <div
                          className={
                            shareSound === sound.id
                              ? `${styles.share} ${styles.show}`
                              : `${styles.share} ${styles.hide}`
                          }
                        >
                          <Share
                            type="album"
                            id={sound.id}
                            url={`https://sdn.lv/s${sound.id}`}
                            text={`${sound.title} by ${sound.user.name}`}
                          />
                        </div>
                        {sound.sales &&
                          sound.sales.on &&
                          !downloadId &&
                          !soundDownloadIds[i] &&
                          !isMine && (
                          <button
                            type="button"
                            className="btn-chromeless tx-black tx-12 mr-3"
                            onClick={() => buyTrack(sound)}
                          >
                            <i className="solicon-tag-cord mr-2" />
                            <FormattedMessage
                              id="app.buy.track"
                              defaultMessage="Buy this track"
                            />
                          </button>
                        )}
                        {soundDownloadIds[i] && (
                          <button
                            type="button"
                            className="btn-chromeless tx-black tx-12 mr-3"
                            onClick={() => {
                              Router.push(
                                `/download?id=${soundDownloadIds[i]}`,
                                `/download/${soundDownloadIds[i]}`,
                              )
                            }}
                          >
                            <i className="solicon-tag-cord mr-2" />
                            <FormattedMessage
                              id="download.button"
                              defaultMessage="Download"
                            />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
            <div className="mb-5">
              <h2 className="heading">
                <span className="text">Comments</span>
              </h2>
              <Comments type="album" id={album.id} owner={album.user.id} />
            </div>
          </div>{' '}
          {/* end left column */}
        </div>
      </div>
      <BuyModal
        category="album"
        data={album}
        isOpen={isBuyOpen}
        onClose={() => setBuyOpen(false)}
      />
      <BuyModal
        category="sound"
        data={buySound}
        isOpen={isSoundBuyOpen}
        onClose={() => setSoundBuyOpen(false)}
      />
    </div>
  )
}

Album.defaultProps = {}

export default Album
